// File: packages/apps/workoutsmart/src/services/locationFeatureApi.js
import authService from '@platform/core/src/services/authService';
import config from '@platform/core/src/config';

const API_BASE_URL = config.API_BASE_URL;

export const searchLocationFeatures = async (payload) => {
  const url = `${API_BASE_URL}/api/v1/location-features/search`;
  try {
    const response = await authService.authFetch(url, {
      method: 'POST',
      body: payload,
    });
    if (!response.ok) {
      let errorDetail = `Failed to search location features: ${response.status}`;
      try {
        const errorData = await response.json();
        errorDetail = errorData.detail || errorDetail;
      } catch (e) { }
      throw new Error(errorDetail);
    }
    return await response.json();
  } catch (error) {
    console.error('[workoutsmart/locationFeatureApi] Error searching location features:', error);
    return [];
  }
};

/**
 * Fetches the list of all available location features.
 * @param {Object} [options={}] - Query options.
 * @param {string} [options.scope] - The scope of resources to return (e.g., 'personalized').
 * @param {string} [options.fields] - Comma-separated list of fields to return.
 * @returns {Promise<Array<Object>>} An array of location feature objects.
 */
export const getLocationFeatures = async (options = {}) => {
  const url = new URL(`${API_BASE_URL}/api/v1/location-features`);
  if (options.scope) {
    url.searchParams.append('scope', options.scope);
  }
  if (options.fields) {
    url.searchParams.append('fields', options.fields);
  }

  try {
    const response = await authService.authFetch(url.toString());
    if (!response.ok) {
      let errorDetail = `Failed to fetch location features: ${response.status}`;
      try {
        const errorData = await response.json();
        errorDetail = errorData.detail || errorDetail;
      } catch (e) {  }
      throw new Error(errorDetail);
    }
    const features = await response.json();
    
    if (options.fields) {
        return features.map(f => ({ ...f, _isPartial: true }));
    }

    return features;
  } catch (error) {
    console.error('[workoutsmart/locationFeatureApi] Error fetching location features:', error);
    authService.trackNetworkError('getLocationFeatures', error);
    throw error;
  }
};

/**
 * Fetches a single location feature by ID.
 * @param {string} id - The ID of the location feature.
 * @returns {Promise<Object>} The location feature object.
 */
export const getLocationFeatureById = async (id) => {
  const url = `${API_BASE_URL}/api/v1/location-features/${id}`;
  try {
    const response = await authService.authFetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch location feature: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('[workoutsmart/locationFeatureApi] Error fetching location feature by ID:', error);
    authService.trackNetworkError('getLocationFeatureById', error);
    throw error;
  }
};

export const createLocationFeature = async (featureData) => {
  const url = `${API_BASE_URL}/api/v1/location-features`;
  try {
    const response = await authService.authFetch(url, {
      method: 'POST',
      body: featureData,
    });
    if (!response.ok) {
      let errorDetail = `Failed to create location feature: ${response.status}`;
      try {
        const errorData = await response.json();
        errorDetail = errorData.detail || errorDetail;
      } catch (e) {  }
      throw new Error(errorDetail);
    }
    return await response.json();
  } catch (error) {
    console.error('[workoutsmart/locationFeatureApi] Error creating location feature:', error);
    authService.trackNetworkError('createLocationFeature', error);
    throw error;
  }
};

export const updateLocationFeature = async (featureId, featureData) => {
  const url = `${API_BASE_URL}/api/v1/location-features/${featureId}`;
  try {
    const response = await authService.authFetch(url, {
      method: 'PUT',
      body: featureData,
    });
    if (!response.ok) {
      let errorDetail = `Failed to update location feature: ${response.status}`;
      try {
        const errorData = await response.json();
        errorDetail = errorData.detail || errorDetail;
      } catch (e) {  }
      throw new Error(errorDetail);
    }
    return await response.json();
  } catch (error) {
    console.error('[workoutsmart/locationFeatureApi] Error updating location feature:', error);
    authService.trackNetworkError('updateLocationFeature', error);
    throw error;
  }
};

export const deleteLocationFeature = async (featureId) => {
  const url = `${API_BASE_URL}/api/v1/location-features/${featureId}`;
  try {
    const response = await authService.authFetch(url, {
      method: 'DELETE',
    });
    if (!response.ok) {
      let errorDetail = `Failed to delete location feature: ${response.status}`;
      try {
        const errorData = await response.json();
        errorDetail = errorData.detail || errorDetail;
      } catch (e) {  }
      throw new Error(errorDetail);
    }
  } catch (error) {
    console.error('[workoutsmart/locationFeatureApi] Error deleting location feature:', error);
    authService.trackNetworkError('deleteLocationFeature', error);
    throw error;
  }
};

export const getLocationFeatureDetails = async (featureId) => {
  const url = `${API_BASE_URL}/api/v1/location-features/${featureId}/details`;
  try {
    const response = await authService.authFetch(url);
    if (response.status === 404) {
      return null;
    }
    if (!response.ok) {
      let errorDetail = `Failed to fetch location feature details: ${response.status}`;
      try {
        const errorData = await response.json();
        errorDetail = errorData.detail || errorDetail;
      } catch (e) {  }
      throw new Error(errorDetail);
    }
    return await response.json();
  } catch (error) {
    console.error(`[workoutsmart/locationFeatureApi] Error fetching location feature details for ${featureId}:`, error);
    throw error;
  }
};

export const completeLocationFeature = async (partialData) => {
  const url = `${API_BASE_URL}/api/v1/location-features/complete`;
  try {
    const response = await authService.authFetch(url, {
      method: 'POST',
      body: partialData,
    });
    if (!response.ok) {
      let errorDetail = `Failed to complete location feature: ${response.status}`;
      try {
        const errorData = await response.json();
        errorDetail = errorData.detail || errorDetail;
      } catch (e) {  }
      throw new Error(errorDetail);
    }
    return await response.json();
  } catch (error) {
    console.error('[workoutsmart/locationFeatureApi] Error completing location feature:', error);
    authService.trackNetworkError('completeLocationFeature', error);
    throw error;
  }
};